import * as React from 'react'

// Shared grip-drag lifecycle for the card + docked-toolbar grips: capture, window
// move/up/cancel, grabbing cursor/no-select, symmetric teardown. pointercancel matters —
// a bare pointerup misses touch gesture-takeover, leaving the drag armed + cursor stuck.
export interface IGripDrag {
  // Bind to the grip element's onPointerDown.
  readonly onPointerDown: (e: React.PointerEvent) => void
  // Force-teardown from outside the pointer flow (stopAnchoring / dispose / unmount).
  readonly stop: () => void
}

export function createGripDrag(handlers: {
  onStart?: (e: React.PointerEvent) => void
  onMove: (e: PointerEvent) => void
  onEnd?: () => void
}): IGripDrag {
  let target: HTMLElement = null

  const move = (e: PointerEvent): void => handlers.onMove(e)

  const stop = (): void => {
    if (!target) {
      return
    }
    target.style.cursor = ''
    target = null
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', stop)
    window.removeEventListener('pointercancel', stop)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
    handlers.onEnd?.()
  }

  const onPointerDown = (e: React.PointerEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    stop() // never stack two drags
    target = e.currentTarget as HTMLElement
    handlers.onStart?.(e)
    // Capture keeps the drag alive if the cursor leaves the window; captured events
    // still reach the window listeners that drive it.
    try {
      target.setPointerCapture(e.pointerId)
    } catch {
      // best-effort — the window listeners drive the drag regardless
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', stop)
    window.addEventListener('pointercancel', stop)
    target.style.cursor = 'grabbing'
    document.body.style.cursor = 'grabbing'
    document.body.style.userSelect = 'none'
  }

  return { onPointerDown, stop }
}
